package fr.frux.feature.map

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBars
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.maps.MAP_ATTRIBUTION_ROW_HEIGHT

/**
 * Zones reservees de la carte, declaratives (#163). Remplace le Box a calages mesures ou les
 * bandeaux d'info et la pastille de re-requete etaient ancres sur `topControlsHeight`, une hauteur
 * mesuree de la barre de recherche : deux piles hautes calees l'une sur l'autre par mesure croisee,
 * fragile des qu'un bandeau de plus s'ajoutait.
 *
 * [topBar] et [topNotices] vivent dans la MEME `Column` : le second empile toujours SOUS le
 * premier par construction, sans mesure. Idem pour [bottomStart]/[bottomEnd]/[bottomSlot], places
 * dans une colonne commune ancree en bas : les actions carte se retrouvent toujours juste
 * au-dessus du contenu du slot bas (fiche d'arbre, groupe de marqueurs), qu'il soit vide ou non,
 * sans plus jamais mesurer sa hauteur pour un `maxOf` avec la clearance de la bottom nav.
 *
 * Hors de ce scaffold : feuilles modales, snackbar, overlays plein ecran (loading/erreur) — ils ne
 * se disputent l'espace avec rien d'autre, un simple `Box.align` suffit (cf. [MapScreen]).
 *
 * @param bottomSlot contenu du slot bas, dans une boite bornee par [bottomSlotMaxWidth] (grand
 *   ecran) et non par la largeur de l'ecran : un `Modifier.align` pose a l'interieur de [bottomSlot]
 *   s'aligne dans cette boite, pas sur l'ecran entier.
 * @param onBottomSlotHeightChange hauteur de la zone du slot bas (feuille comprise, du bord bas de
 *   l'ecran a son sommet), rapportee a chaque changement : l'ecran en tire le decalage de
 *   l'attribution du fond de carte (ADR 0010), qui doit passer au-dessus de la feuille.
 * @param bottomClearance hauteur minimale de la zone du slot bas : slot vide, elle tient
 *   [bottomStart]/[bottomEnd] au-dessus de la bottom nav ; slot occupe, la feuille impose sa
 *   propre hauteur et touche le bord bas (elle glisse derriere la nav, son padding interne
 *   protege son contenu). Valeur unique : plus de `maxOf` duplique entre les deux colonnes
 *   d'actions.
 */
@Composable
fun MapOverlayScaffold(
    topBar: @Composable ColumnScope.() -> Unit,
    topNotices: @Composable ColumnScope.() -> Unit,
    bottomSlot: @Composable BoxScope.() -> Unit,
    bottomStart: @Composable RowScope.() -> Unit,
    bottomEnd: @Composable RowScope.() -> Unit,
    bottomClearance: Dp,
    modifier: Modifier = Modifier,
    topMaxWidth: Dp = Dp.Unspecified,
    bottomSlotMaxWidth: Dp = Dp.Unspecified,
    onBottomSlotHeightChange: (Dp) -> Unit = {},
) {
    val density = LocalDensity.current
    Box(modifier = modifier.fillMaxSize()) {
        // Grand ecran : la colonne du haut se cale au debut de la zone carte et se borne, au lieu
        // de s'etirer sur toute la largeur (spec §5). `widthIn` AVANT `fillMaxWidth` : le second
        // remplit ce que le premier a borne.
        val topBounded = topMaxWidth != Dp.Unspecified
        Column(
            modifier =
                Modifier
                    .align(if (topBounded) Alignment.TopStart else Alignment.TopCenter)
                    .padding(start = if (topBounded) FruxSpacing.sm else 0.dp)
                    .widthIn(max = topMaxWidth)
                    .fillMaxWidth()
                    .windowInsetsPadding(WindowInsets.statusBars),
        ) {
            // `fillMaxWidth` sur chaque sous-colonne : les enfants se centrent avec
            // `Modifier.align(Alignment.CenterHorizontally)`, qui n'a d'effet que si la colonne
            // occupe la largeur de l'ecran (sinon elle epouse son contenu et tout se cale a gauche).
            Column(modifier = Modifier.fillMaxWidth(), content = topBar)
            Column(modifier = Modifier.fillMaxWidth(), content = topNotices)
        }

        Column(modifier = Modifier.align(Alignment.BottomCenter).fillMaxWidth()) {
            // Rangee d'actions basse : legende+attribution a gauche, liste+localiser a droite.
            // `SpaceBetween` plutot que deux ancrages BottomStart/BottomEnd independants sur le Box
            // plein ecran — c'est ce qui permet a cette rangee de rester au-dessus de [bottomSlot]
            // sans jamais mesurer sa hauteur.
            //
            // Degagement bas de la hauteur du bouton d'attribution que le SDK dessine sous
            // la carte : sans lui, les actions de droite recouvrent le bouton et son panneau.
            // `camera.contentPadding` ne l'inclut pas (il ne decrit que le chrome de l'ecran),
            // donc rien n'est compte deux fois.
            Row(
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Bottom,
                modifier = Modifier.fillMaxWidth().padding(bottom = MAP_ATTRIBUTION_ROW_HEIGHT),
            ) {
                Row(verticalAlignment = Alignment.Bottom, content = bottomStart)
                Row(verticalAlignment = Alignment.Bottom, content = bottomEnd)
            }

            // `heightIn(min)` et non un Spacer sous le slot : une feuille du slot doit toucher le
            // bord bas de l'ecran (elle glisse DERRIERE la bottom nav flottante, son padding
            // interne protege deja son contenu — cf. MapSheetSurface). Le minimum ne sert que
            // slot vide, pour garder les actions au-dessus de la nav : meme semantique que
            // l'ancien `maxOf(clearance, hauteur mesuree du slot)`, sans mesure.
            //
            // Slot bas borne et centre en grand ecran : l'enveloppe garde la pleine largeur (et le
            // `heightIn` qui tient les actions au-dessus de la nav), seule la boite de contenu se
            // borne.
            Box(
                contentAlignment = Alignment.BottomCenter,
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .heightIn(min = bottomClearance)
                        .onSizeChanged { size -> onBottomSlotHeightChange(with(density) { size.height.toDp() }) },
            ) {
                Box(
                    contentAlignment = Alignment.BottomCenter,
                    // Borné = grand écran : la feuille flotte à une marge du bord bas au lieu de
                    // le toucher (sur téléphone elle glisse derrière la barre du bas).
                    modifier =
                        Modifier
                            .widthIn(max = bottomSlotMaxWidth)
                            .fillMaxWidth()
                            .padding(bottom = if (bottomSlotMaxWidth != Dp.Unspecified) FruxSpacing.md else 0.dp),
                    content = bottomSlot,
                )
            }
        }
    }
}

/** Largeurs du chrome carte en grand ecran (spec 2026-09-10 §5). */
internal object MapChromeDefaults {
    /** Recherche, bouton de filtres et puces, flottants en haut a gauche de la zone carte. */
    val ExpandedTopWidth: Dp = 480.dp

    /** Fiche flottante et groupe de marqueurs : meme silhouette que les feuilles modales M3. */
    val ExpandedBottomSlotWidth: Dp = 640.dp
}
